import { IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonList, IonRow } from '@ionic/react'
import { BaseButton, DatePicker, DatesPickerModal, EdisuPopover, Rating, TotalPrice } from 'components'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IBookingBoxProps } from './booking-box-props.interface'
import { icon_arrow_dx, icon_calendar, icon_close, icon_code, icon_persone, icon_plus } from 'icons'
import './booking-box.scss'
import { ServicesModal } from 'components'
import { dateService, routeService, numberService } from 'services'
import { motion } from 'framer-motion'
import { addDays } from 'date-fns'
import { Link } from 'react-router-dom'
import { useProfileTypes } from 'hooks'

const animation_variants = {
  open: {
    opacity: 1,
    height: 'auto',
  },
  collapsed: { opacity: 0, height: 0 },
}

const BookingBoxAnimation: React.FC<{ open: boolean; children: React.ReactNode; transitionDuration?: number }> = ({
  open,
  children,
  transitionDuration = 0.4,
}) => {
  return (
    <motion.div
      initial={open ? 'open' : 'collapsed'}
      animate={open ? 'open' : 'collapsed'}
      inherit={false}
      variants={animation_variants}
      transition={{
        ease: open ? 'easeOut' : 'easeIn',
        duration: transitionDuration,
      }}
    >
      {children}
    </motion.div>
  )
}

const BookingBox: React.FC<IBookingBoxProps> = ({
  enableBookNowButton,
  services,
  priceMin,
  dateFrom,
  dateTo,
  rating,
  bookFunction,
  backdropFunction,
  selectedRooms,
  numDays,
  selectedServices,
  profile,
  eventCode,
  noAvailability,
  guestsSelection,
  setSelectedServices,
  enableChangeDates,
  changeFrom,
  changeTo,
}) => {
  const { t } = useTranslation()
  const [collapsedMobile, setCollapsedMobile] = useState(true)
  const [serviceModalOpen, setServiceModalOpen] = useState(false)
  const [datesPickerModalOpen, setDatesPickerModalOpen] = useState(false)
  const onChangeMobileDates = (dateFrom?: Date | undefined, dateTo?: Date | undefined) => {
    if (dateFrom && changeFrom) changeFrom(dateFrom)
    if (dateTo && changeTo) changeTo(dateTo)
  }

  const collapseMobileFunction = (b: boolean) => {
    setCollapsedMobile(b)
    !!backdropFunction && backdropFunction(!b)
  }

  const onMobileClick = () => {
    setDatesPickerModalOpen(true)
  }
  const profileTypes = useProfileTypes()

  const now = new Date()

  const totalPrice = numberService.getTotalPrice(selectedRooms, selectedServices)

  const profileDescription = `${t('profile')} ${profileTypes.find((x) => x.id === profile)?.descrizione}`

  let costSelectedServices = 0

  if (selectedServices && selectedServices.length > 0) {
    costSelectedServices = selectedServices.reduce((prev, current) => {
      return prev + current.prezzo
    }, 0)
  }

  const dateToPopup = (
    <EdisuPopover submitted={false} triggerId="date-to" extraClassName="dates-popover-cnt ion-hide-sm-down">
      <DatePicker onChange={changeTo} value={dateTo} minDate={dateFrom && addDays(dateFrom, 1)} />
    </EdisuPopover>
  )
  const dateFromPopup = (
    <EdisuPopover submitted={false} triggerId="date-from" extraClassName="dates-popover-cnt ion-hide-sm-down">
      <DatePicker onChange={changeFrom} value={dateFrom} minDate={addDays(now, 1)} />
    </EdisuPopover>
  )

  const contentBoxLgUp = (
    <>
      <div className={`ion-hide-lg-up ion-text-right ${collapsedMobile ? '' : 'marginTop'}`}>
        <IonIcon icon={icon_close} onClick={() => collapseMobileFunction(true)} />
      </div>
      <div>
        <span className="edisu-label">{profileDescription}</span>
      </div>
      <div className="box-header box-bordered flex-jbetween-acenter">
        <p className="price-line">
          {!!priceMin && (
            <>
              {t('starting_from')}{' '}
              <span>{priceMin % numDays === 0 ? priceMin / numDays : (priceMin / numDays).toFixed(2).replace('.', ',')} €</span> /{' '}
              {t('per_night')}
            </>
          )}
        </p>
        <Rating value={rating} />
      </div>

      <div className="box-content box-bordered">
        <IonGrid className="ion-no-padding">
          <IonRow>
            <IonCol size="6">
              <IonItem lines="none" className="item-container">
                <IonLabel position="stacked" className="ion-text-uppercase edisu-label">
                  {t('check_in')}
                </IonLabel>
                <IonItem lines="none" className="item-detail">
                  <IonLabel className="ion-text-capitalize ion-hide-sm-down" id="date-from-trigger">
                    {dateService.formatDatePickerPlaceholder(dateFrom)}{' '}
                  </IonLabel>
                  <IonLabel
                    className={`ion-text-capitalize  ion-hide-md-up ${enableChangeDates ? 'clickable' : ''}`}
                    onClick={enableChangeDates ? () => onMobileClick() : undefined}
                  >
                    {dateService.formatDatePickerPlaceholder(dateFrom)}
                  </IonLabel>
                  <IonIcon icon={icon_calendar} slot="end"></IonIcon>
                </IonItem>
              </IonItem>
            </IonCol>
            <IonCol size="6">
              <IonItem lines="none" className="item-container">
                <IonLabel position="stacked" className="ion-text-uppercase edisu-label">
                  {t('check_out')}
                </IonLabel>
                <IonItem lines="none" className="item-detail">
                  <IonLabel className="ion-text-capitalize ion-hide-sm-down" id="date-to-trigger">
                    {dateService.formatDatePickerPlaceholder(dateTo)}
                  </IonLabel>
                  <IonLabel
                    className={`ion-text-capitalize  ion-hide-md-up ${enableChangeDates ? 'clickable' : ''}`}
                    onClick={enableChangeDates ? () => onMobileClick() : undefined}
                  >
                    {dateService.formatDatePickerPlaceholder(dateTo)}{' '}
                  </IonLabel>

                  <IonIcon icon={icon_calendar} slot="end"></IonIcon>
                </IonItem>
              </IonItem>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="6">
              <IonItem lines="none" className="item-container">
                <IonLabel position="stacked" className="ion-text-uppercase edisu-label">
                  {t('people')}
                </IonLabel>
                <IonItem lines="none" detail={true} detailIcon={icon_arrow_dx} className="item-detail clickable">
                  <IonLabel className="ion-text-capitalize">{!!guestsSelection && guestsSelection[0]} </IonLabel>
                  <IonIcon icon={icon_persone} slot="start"></IonIcon>
                </IonItem>
              </IonItem>
            </IonCol>
            <IonCol size="6">
              <IonItem lines="none" className="item-container">
                <IonLabel position="stacked" className="ion-text-uppercase edisu-label">
                  {t('event_code')}
                </IonLabel>
                <IonItem lines="none" className="item-detail ">
                  <IonLabel className="ion-text-uppercase">{eventCode} </IonLabel>
                  <IonIcon icon={icon_code} slot="start"></IonIcon>
                </IonItem>
              </IonItem>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      {noAvailability && (
        <div className="box-bottom">
          <p>{t('no_search_results')}</p>
          <Link to={routeService.generateRoutePath('/')} className="reset-search">
            {t('new_search')}
          </Link>
        </div>
      )}

      {!noAvailability && (
        <>
          {/*TODO bloccata per ora la scelta dei servizi*/}
          <BookingBoxAnimation open={selectedRooms.length > 0}>
            {false && (
              <div className="box-services box-bordered">
                <BaseButton
                  extraClassName="add-services"
                  label={t('manage_services')}
                  onClickFunction={() => setServiceModalOpen(true)}
                  fill="outline"
                  icon={icon_plus}
                />
              </div>
            )}

            <div className="box-rooms box-bordered">
              {selectedRooms.map((sr, i) => {
                return (
                  <div className="ion-no-padding" key={sr.room.id + '' + i}>
                    <BookingBoxAnimation open={selectedServices.length > 0}>
                      <div className="room-summary">
                        <span className="edisu-text-normal">{t('selected_services')}</span>
                        <span className="edisu-text-normal price">{costSelectedServices} €</span>
                      </div>
                    </BookingBoxAnimation>
                    <div className="room-summary">
                      <span className="edisu-text-normal">
                        {sr.value > 1 ? sr.value + ' ' + t('more_beds') : sr.value + ' ' + t('one_bed')}
                        in {sr.room.descrizione} {numDays > 1 ? numDays + ' ' + t('nights') : numDays + ' ' + t('night')}
                      </span>
                      <span className="edisu-text-normal price">{sr.value * sr.room.prezzo} €</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </BookingBoxAnimation>

          <div className="box-bottom">
            <BookingBoxAnimation open={selectedRooms.length > 0}>
              <TotalPrice price={totalPrice} />
            </BookingBoxAnimation>
            <BaseButton
              extraClassName="book-now-btn"
              label={t('book_now')}
              onClickFunction={() => {
                !!bookFunction && bookFunction()
              }}
              disabled={!enableBookNowButton}
            />
            <p className="finalize-text">{t('finalize_book_next_steps')}</p>
            <Link to={routeService.generateRoutePath('/')} className="reset-search">
              {t('new_search')}
            </Link>
          </div>
        </>
      )}
    </>
  )

  return (
    <>
      <div className={'edisu-booking-box ion-hide-lg-down'}>{contentBoxLgUp}</div>

      <div className={'edisu-booking-box mobile ion-hide-lg-up'}>
        {collapsedMobile && (
          <>
            <div className="mobile-box">
              <div className="left-cnt">
                {!noAvailability && selectedRooms.length === 0 && (
                  <p className="price-line">
                    {!!priceMin && (
                      <>
                        {t('starting_from')} <span>{priceMin} €</span> / {t('per_night')}
                      </>
                    )}
                  </p>
                )}{' '}
                {!noAvailability && selectedRooms.length > 0 && (
                  <div className="total-price-cnt">
                    <span className="total-price">{totalPrice} €</span>
                  </div>
                )}
                {noAvailability && (
                  <div>
                    <p>
                      {dateService.formatDatePickerPlaceholder(dateFrom)} - {dateService.formatDatePickerPlaceholder(dateTo)}
                    </p>
                    <p>
                      {t('no_search_results')}
                      <br />
                      <Link to={routeService.generateRoutePath('/')} className="reset-search">
                        {t('new_search')}
                      </Link>
                    </p>
                  </div>
                )}
                {!noAvailability && (
                  <span>
                    {dateService.formatDatePickerPlaceholder(dateFrom)} - {dateService.formatDatePickerPlaceholder(dateTo)}
                  </span>
                )}
                <IonList className="box-content-lg-down ion-no-padding">
                  <IonItem lines="none" className="item-detail ">
                    <IonLabel className="ion-text-capitalize">{t('people') + ': ' + (!!guestsSelection && guestsSelection[0])} </IonLabel>
                    <IonIcon icon={icon_persone} slot="start"></IonIcon>
                  </IonItem>

                  {eventCode && (
                    <IonItem lines="none" className="item-detail ">
                      <IonLabel className="ion-text-uppercase">{eventCode} </IonLabel>
                      <IonIcon icon={icon_code} slot="start"></IonIcon>
                    </IonItem>
                  )}
                </IonList>
              </div>

              <BaseButton
                label={t('book_now')}
                disabled={!enableBookNowButton || noAvailability}
                onClickFunction={
                  selectedRooms.length === 0
                    ? () => {
                        !!bookFunction && bookFunction()
                      }
                    : () => collapseMobileFunction(false)
                }
              />
            </div>
          </>
        )}

        <BookingBoxAnimation open={!collapsedMobile}>
          <div className="animated-cnt">{contentBoxLgUp}</div>
        </BookingBoxAnimation>
      </div>

      <ServicesModal
        services={services}
        selectedServices={selectedServices}
        isOpen={serviceModalOpen}
        setIsOpen={setServiceModalOpen}
        onConfirmServices={(s) => setSelectedServices(s)}
      />

      {/* {guestsSelection && <GuestsModal isOpen={guestsModalOpen} setIsOpen={setGuestsModalOpen} guestsSelections={guestsSelection} />} */}

      {enableChangeDates && dateFrom && dateTo && (
        <DatesPickerModal
          dateFrom={dateFrom}
          dateTo={dateTo}
          onConfirm={onChangeMobileDates}
          isOpen={datesPickerModalOpen}
          setIsOpen={setDatesPickerModalOpen}
        />
      )}

      {enableChangeDates && dateToPopup}
      {enableChangeDates && dateFromPopup}
    </>
  )
}

export default BookingBox
